import React from "react";

import { AuthenticationProvider } from "@arup-group/cognito-authenticator";
import { Auth } from "@aws-amplify/auth";
import { AxiosRequestConfig } from "axios";

import { apiClient } from "api";
import { envConfig } from "config/env";

type WithApiKeyProps = {
	apiKey?: string;
};
export const WithApiKeyOrUserAuth = <P extends WithApiKeyProps>(Component: React.FC<P>) => {
	const apiKey = new URLSearchParams(window.location.search).get("api_key") || undefined;

	const cognitoInterceptor = async (request: AxiosRequestConfig) => {
		const session = await Auth.currentSession();
		if (request.headers) request.headers["Authorization"] = `Bearer ${session.getIdToken().getJwtToken()}`;
		return request;
	};
	const apiKeyInterceptor = async (request: AxiosRequestConfig) => {
		if (request.headers) request.headers["Authorization"] = `Api-Key ${apiKey}`;
		return request;
	};

	const HOC = (props: P) => {
		React.useLayoutEffect(() => {
			const interceptor = apiClient.interceptors.request.use(
				apiKey ? apiKeyInterceptor : cognitoInterceptor,
				Promise.reject,
				{
					runWhen: (r) => !hasAuthHeader(r),
				},
			);
			console.log("Added axios interceptor", interceptor);
			return () => {
				console.log("Ejecting axios interceptor", interceptor);
				apiClient.interceptors.request.eject(interceptor);
			};
		}, []);

		if (apiKey)
			return (
				<React.Fragment>
					<Component apiKey={apiKey} {...props} />
				</React.Fragment>
			);
		return (
			<AuthenticationProvider
				region={envConfig.AWS_REGION}
				userPoolId={envConfig.AWS_POOL_ID}
				userPoolWebClientId={envConfig.AWS_WEB_CLIENT_ID}
				domain={envConfig.AWS_COGNITO_DOMAIN}
				allowSignUp={false}
				azureADProviderId="AzureAD"
			>
				<Component {...props} />
			</AuthenticationProvider>
		);
	};
	HOC.displayName = `WithApiKeyOrUserAuth${Component.displayName}`;
	return HOC;
};

const hasAuthHeader = (r: AxiosRequestConfig) => "Authorization" in (r.headers || {});
